import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { TruckIcon, MapPinIcon, PlusIcon } from '@heroicons/react/24/outline';
import { sosResponderApi } from '../../services/api';
import { usePlatoonStore } from '../../store/platoonStore';
import type { SOSEvent } from '../../store/realtimeStore';

interface Responder {
  id: string;
  responderId: string;
  responderType: string;
  callsign?: string | null;
  assignedAt?: string;
  enRouteAt?: string | null;
  arrivedAt?: string | null;
}

function safeTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'HH:mm:ss');
}

export default function ResponderPanel({ sos }: { sos: SOSEvent }) {
  const queryClient = useQueryClient();
  const platoons = usePlatoonStore((s) => s.platoons);
  const fetchPlatoons = usePlatoonStore((s) => s.fetchPlatoons);
  const [pick, setPick] = useState('');

  useEffect(() => {
    if (platoons.length === 0) fetchPlatoons();
  }, [platoons.length, fetchPlatoons]);

  const responders = (sos.responders ?? []) as Responder[];
  const closed = sos.status === 'RESOLVED' || sos.status === 'CANCELLED';

  const callsignFor = useMemo(() => {
    const byId = new Map(platoons.map((p) => [p.id, p.callsign]));
    return (r: Responder) => r.callsign ?? byId.get(r.responderId) ?? r.responderId.slice(0, 8);
  }, [platoons]);

  // Candidate responders: platoons in the same unit, excluding the platoon in distress.
  const candidates = useMemo(
    () => platoons.filter((p) => (!sos.unitId || p.unitId === sos.unitId) && p.id !== sos.platoonId),
    [platoons, sos.unitId, sos.platoonId]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sos'] });
    queryClient.invalidateQueries({ queryKey: ['sos-history-all'] });
  };

  const assignMutation = useMutation({
    mutationFn: () => sosResponderApi.assign(sos.id, pick),
    onSuccess: () => { toast.success('Responder assigned'); setPick(''); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to assign responder'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ rid, status }: { rid: string; status: 'EN_ROUTE' | 'ARRIVED' }) =>
      sosResponderApi.setStatus(sos.id, rid, status),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to update responder'),
  });

  return (
    <div className="mt-6 pt-6 border-t border-command-border">
      <h3 className="text-sm font-tactical text-gray-400 mb-3">RESPONDERS ({responders.length})</h3>

      <div className="space-y-2">
        {responders.map((r) => {
          const stage = r.arrivedAt ? 'ARRIVED' : r.enRouteAt ? 'EN_ROUTE' : 'ASSIGNED';
          return (
            <div key={r.id} className="flex items-center gap-2 rounded-lg border border-command-border bg-command-bg p-2.5">
              <span className="text-sm font-semibold text-gray-100">{callsignFor(r)}</span>
              <span className={clsx(
                'px-2 py-0.5 rounded-full text-[10px] font-bold',
                stage === 'ARRIVED' && 'bg-command-success/20 text-command-success',
                stage === 'EN_ROUTE' && 'bg-command-info/20 text-command-info',
                stage === 'ASSIGNED' && 'bg-severity-medium/20 text-severity-medium'
              )}>
                {stage === 'ARRIVED' ? `ARRIVED ${safeTime(r.arrivedAt)}` : stage === 'EN_ROUTE' ? `EN ROUTE ${safeTime(r.enRouteAt)}` : 'ASSIGNED'}
              </span>
              {!closed && (
                <div className="ml-auto flex items-center gap-1">
                  {!r.enRouteAt && (
                    <button type="button" onClick={() => statusMutation.mutate({ rid: r.id, status: 'EN_ROUTE' })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-command-info/20 text-command-info hover:bg-command-info/30">
                      <TruckIcon className="w-3.5 h-3.5" /> En route
                    </button>
                  )}
                  {!r.arrivedAt && (
                    <button type="button" onClick={() => statusMutation.mutate({ rid: r.id, status: 'ARRIVED' })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-command-success/20 text-command-success hover:bg-command-success/30">
                      <MapPinIcon className="w-3.5 h-3.5" /> Arrived
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {responders.length === 0 && <p className="text-xs text-gray-500">No responders assigned yet.</p>}
      </div>

      {!closed && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)}
            className="px-2 py-1.5 rounded bg-command-bg border border-command-border text-xs text-gray-100">
            <option value="">Assign platoon…</option>
            {candidates.map((p) => <option key={p.id} value={p.id}>{p.callsign}</option>)}
          </select>
          <button type="button" onClick={() => assignMutation.mutate()} disabled={!pick || assignMutation.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90 disabled:opacity-50">
            <PlusIcon className="w-4 h-4" /> Assign
          </button>
        </div>
      )}
    </div>
  );
}
